import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Inbox,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchAllChangeRequests,
  reviewChangeRequest,
} from "@/services/employeeChangeRequestService";
import {
  ChangeRequestStatusBadge,
  formatRequestDate,
} from "@/components/common/ChangeRequestStatusBadge";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

// "All" leads because it is the default — the full history is the useful
// starting view, and pending items are already surfaced by the header badge.
const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

/**
 * `embedded` drops the page-level heading, for use as a tab inside the
 * Employees page (which already supplies one). `onCountChange` reports the
 * pending total back so the tab can show a badge.
 */
const EmployeeChangeRequests = ({ embedded = false, onCountChange, onBack }) => {
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);
  const canReview = canEdit("employee_change_requests");

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actioningId, setActioningId] = useState(null);

  // Reject needs a reason, so it goes through a dialog rather than a bare click.
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Raised when the live value moved after the request was filed (HTTP 409).
  const [conflict, setConflict] = useState(null);

  const loadRequests = async (status = statusFilter) => {
    setLoading(true);
    try {
      const data = await fetchAllChangeRequests(
        status === "all" ? undefined : status,
      );
      const rows = Array.isArray(data) ? data : [];
      setRequests(rows);
      // Report the pending total to the parent tab. When a narrower filter is
      // active the list is not the whole picture, so only count from a view
      // that actually contains every pending row.
      if (onCountChange && (status === "pending" || status === "all")) {
        onCountChange(rows.filter((row) => row.status === "pending").length);
      }
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load change requests"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the status filter changes. loadRequests is intentionally
  // not a dependency — it is re-created each render and would loop.
  useEffect(() => {
    loadRequests(statusFilter);
  }, [statusFilter]);

  // After a decision the badge must be right regardless of which filter is on
  // screen. When the visible rows already contain every pending request,
  // loadRequests derives the count for free; otherwise ask for it directly.
  const syncPendingCount = async () => {
    if (!onCountChange) return;
    if (statusFilter === "pending" || statusFilter === "all") return;
    try {
      const rows = await fetchAllChangeRequests("pending");
      onCountChange(Array.isArray(rows) ? rows.length : 0);
    } catch (error) {
      console.error("Failed to refresh pending count:", error);
    }
  };

  const approve = async (request, confirmConflict = false) => {
    setActioningId(request._id);
    try {
      await reviewChangeRequest(request._id, {
        status: "approved",
        ...(confirmConflict ? { confirm_conflict: true } : {}),
      });
      toast.success("Change request approved and applied");
      setConflict(null);
      loadRequests();
      syncPendingCount();
    } catch (error) {
      // 409 means the values moved underneath this request. Surface exactly
      // what changed and make the admin decide, rather than overwriting.
      const body = error?.response?.data;
      if (error?.response?.status === 409 && body?.requires_confirmation) {
        setConflict({ request, conflicts: body.conflicts || [] });
        return;
      }
      toast.error(getErrorMessage(error, "Failed to approve request"));
    } finally {
      setActioningId(null);
    }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    setActioningId(rejectTarget._id);
    try {
      await reviewChangeRequest(rejectTarget._id, {
        status: "rejected",
        rejection_reason: rejectionReason || null,
      });
      toast.success("Change request rejected");
      setRejectTarget(null);
      setRejectionReason("");
      loadRequests();
      syncPendingCount();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to reject request"));
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back button beside the title, matching Add/Edit Employee. Filter sits
          opposite and wraps below on narrow screens. */}
      <div
        className={`flex gap-4 gap-y-4 flex-wrap ${
          embedded
            ? "items-center justify-end"
            : "items-center justify-between"
        }`}
      >
        {!embedded && (
          <div className="flex items-center space-x-4">
            {onBack && (
              <Button
                variant="outline"
                size="icon"
                onClick={onBack}
                data-testid="back-btn"
                aria-label="Back to Employees"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Profile Approvals
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                Approve or reject employee changes to sensitive profile fields.
              </p>
            </div>
          </div>
        )}

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-24 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center space-y-2">
            <Inbox className="w-8 h-8 mx-auto text-gray-400" />
            <p className="text-sm text-muted-foreground">
              No {statusFilter === "all" ? "" : statusFilter} change requests.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card key={request._id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <span>{request.employee_name || "Unknown employee"}</span>
                    {request.employee_code && (
                      <span className="text-xs font-normal text-muted-foreground">
                        ({request.employee_code})
                      </span>
                    )}
                  </CardTitle>

                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {formatRequestDate(request.requested_at)}
                    </span>
                    <ChangeRequestStatusBadge status={request.status} />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Wide content scrolls inside its own box rather than the page */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                        <th className="py-2 pr-4 font-semibold">Field</th>
                        <th className="py-2 pr-4 font-semibold">
                          Current Value
                        </th>
                        <th className="py-2 font-semibold">Requested Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(request.changes || []).map((change) => (
                        <tr
                          key={change.field}
                          className="border-t border-gray-100 dark:border-gray-800"
                        >
                          <td className="py-2 pr-4 font-medium text-gray-900 dark:text-gray-200">
                            {change.label}
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground break-all">
                            {change.old_value || "—"}
                          </td>
                          <td className="py-2 text-gray-900 dark:text-gray-200 break-all">
                            {change.new_value || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {request.status === "rejected" && request.rejection_reason && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Rejected: {request.rejection_reason}
                  </p>
                )}

                {request.status === "approved" && request.approved_by?.name && (
                  <p className="text-xs text-muted-foreground">
                    Approved by {request.approved_by.name} on{" "}
                    {formatRequestDate(request.approved_at)}
                  </p>
                )}

                {request.status === "rejected" && request.rejected_by?.name && (
                  <p className="text-xs text-muted-foreground">
                    Rejected by {request.rejected_by.name} on{" "}
                    {formatRequestDate(request.rejected_at)}
                  </p>
                )}

                {request.status === "pending" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      disabled={!canReview || actioningId === request._id}
                      onClick={() => approve(request)}
                    >
                      {actioningId === request._id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canReview || actioningId === request._id}
                      onClick={() => {
                        setRejectTarget(request);
                        setRejectionReason("");
                      }}
                    >
                      Reject
                    </Button>
                    {!canReview && (
                      <span className="text-xs text-muted-foreground">
                        You do not have permission to review requests.
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject — reason is optional but the employee sees it, so prompt for it */}
      <Dialog
        open={Boolean(rejectTarget)}
        onOpenChange={(open) => !open && setRejectTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Change Request</DialogTitle>
            <DialogDescription>
              The employee's current values stay unchanged. Your reason is shown
              to them.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            rows={3}
            placeholder="Reason for rejection (optional)"
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={reject}
              disabled={actioningId === rejectTarget?._id}
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict — the record moved since the request was raised */}
      <Dialog
        open={Boolean(conflict)}
        onOpenChange={(open) => !open && setConflict(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Values Changed Since This Request
            </DialogTitle>
            <DialogDescription>
              These fields were edited after the employee raised this request.
              Approving will overwrite the current values.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {(conflict?.conflicts || []).map((item) => (
              <div
                key={item.field}
                className="border border-amber-200 dark:border-amber-900/50 rounded-md p-3 space-y-1"
              >
                <p className="font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">
                  When requested: {item.value_when_requested || "—"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Value now: {item.value_now || "—"}
                </p>
                <p className="text-xs">
                  Will become: <strong>{item.requested_value || "—"}</strong>
                </p>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)}>
              Cancel
            </Button>
            <Button onClick={() => approve(conflict.request, true)}>
              Approve Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeChangeRequests;
